import { PageHeaderNav, type PageHeaderNavProps } from '@molecules/PageHeaderNav'

import { ChoosingControlOrNav } from '../ChoosingControlOrNav'
import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<PageHeaderNavProps> = {
  items: {
    type: '{ label: string; href: string; current?: boolean; count?: number; legacy?: boolean }[]',
    required: true,
    description:
      'One entry per sibling page. Mark the page being shown with current; count adds a red badge, for items waiting on that page. Mark a legacy (v1) page with legacy so it always renders as a plain anchor.',
  },
  ariaLabel: {
    type: 'string',
    required: true,
    description: 'Names the nav landmark with the section the links belong to, for example "Inbox", not "Navigation".',
  },
  className: { type: 'string', description: 'Extra classes on the nav element.' },
  linkComponent: {
    type: 'ElementType',
    default: "'a'",
    description:
      "What each link renders as. On an Inertia page pass Inertia's Link, so moving to another Inertia page does not reload. Links marked legacy stay plain anchors. On a legacy page leave it as 'a'.",
  },
}

const items = [
  { label: 'SMS', href: '#sms', current: true, count: 4 },
  { label: 'Email', href: '#email' },
]

const doc: ComponentDoc<PageHeaderNavProps> = {
  slug: 'page-header-nav',
  name: 'PageHeaderNav',
  tier: 'molecule',
  description:
    'A row of links to sibling pages, shown in a white strip under a page header. Each link is a real page with its own URL. The current one is underlined across its full width, and a link can carry a count of items waiting on it. For switching views within one page use SegmentedControl instead.',
  importStatement: "import { PageHeaderNav } from '@molecules/PageHeaderNav'",
  sourcePath: 'app/frontend/components/molecules/PageHeaderNav.tsx',
  component: PageHeaderNav,
  props,
  examples: [
    {
      title: 'Two channels',
      render: () => (
        <div className="tw:w-full tw:border-b tw:border-gray-cool-200 tw:bg-white tw:px-6">
          <PageHeaderNav ariaLabel="Inbox" items={items} />
        </div>
      ),
      code: `<PageLayout.Header
  heading="Inbox"
  nav={<PageHeaderNav ariaLabel="Inbox" items={[{ label: 'SMS', href: smsInboxRoute, current: true }, { label: 'Email', href: emailInboxRoute }]} />}
/>`,
    },
  ],
  usage: {
    do: [
      "Pass it to PageLayout.Header through nav. It sits on the header's bottom border.",
      'Use it when the sidebar has one item for several sibling pages, such as the SMS and Email inboxes.',
      'Pass linkComponent={Link} from Inertia on an Inertia page, as the blast lists do. In a strip that mixes v1 and Inertia pages, such as Settings, mark the v1 links legacy.',
    ],
    dont: [
      'Render it with one item. Hide the strip when only one sibling is available.',
      'Use it for in-page content switching. Use SegmentedControl.',
      'Add a third level of links to it. Put those in a SectionNav beside the page content.',
    ],
  },
  choosing: { title: 'Segmented control or page header nav?', content: <ChoosingControlOrNav /> },
  a11y: [
    'It is a nav landmark named by ariaLabel, so screen readers can jump to it and hear what it chooses between.',
    'The current link carries aria-current="page" and is distinguished by an underline as well as colour.',
    'A count is part of the link text, so it is read with the link name.',
  ],
}

export default doc
